import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Power, PowerOff, X } from "lucide-react";

const EMPTY = {
  slug: "", name: "", role_target: "professional",
  monthly_price: 0, currency: "USD", description: "", features: [],
  stripe_price_id: "", is_lifetime: false, active: true, trial_days: 0,
};

export default function AdminPlans() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // plan being edited or null
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/plans");
      setRows(data.rows);
    } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  const toggle = async (id) => {
    try {
      await api.post(`/admin/plans/${id}/toggle`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!confirm("Delete this plan permanently?")) return;
    try {
      await api.delete(`/admin/plans/${id}`);
      toast.success("Plan deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <AdminLayout
      subtitle="Module 9 · Commerce"
      title="Subscription Plans"
      actions={
        <Button onClick={() => { setEditing({ ...EMPTY }); setShowNew(true); }} className="rounded-sm bg-ec-emerald hover:bg-ec-emerald/90 text-ec-ink gap-2" data-testid="plans-new">
          <Plus className="w-4 h-4" /> New plan
        </Button>
      }
    >
      <Card className="rounded-sm border-white/10 bg-white/[0.02]">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="plans-table">
            <thead className="bg-white/5">
              <tr className="text-left">
                <Th>Plan</Th>
                <Th>For</Th>
                <Th>Price / mo</Th>
                <Th>Features</Th>
                <Th>Status</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`plan-row-${p.slug}`}>
                  <Td>
                    <div className="font-semibold text-white">{p.name}</div>
                    <div className="text-xs font-mono-plex text-zinc-500">{p.slug}</div>
                  </Td>
                  <Td><Badge className="bg-white/10 border-white/10 rounded-sm">{p.role_target}</Badge></Td>
                  <Td className="font-display font-bold text-white">${p.monthly_price?.toFixed?.(2) ?? p.monthly_price} <span className="text-xs font-mono-plex font-normal text-zinc-500">{p.currency}</span></Td>
                  <Td className="max-w-[280px]"><div className="text-xs text-zinc-400 line-clamp-2">{(p.features || []).slice(0, 3).join(" · ")}</div></Td>
                  <Td>
                    {p.active
                      ? <Badge className="bg-ec-emerald/10 text-ec-emerald border-ec-emerald/30 rounded-sm">Active</Badge>
                      : <Badge className="bg-white/5 text-zinc-500 border-white/10 rounded-sm">Disabled</Badge>}
                  </Td>
                  <Td className="text-right">
                    <div className="flex justify-end gap-1.5">
                      <IconBtn title="Edit" onClick={() => setEditing(p)} testid={`plan-edit-${p.slug}`}><Edit2 className="w-3 h-3" /></IconBtn>
                      <IconBtn title={p.active ? "Disable" : "Enable"} onClick={() => toggle(p.id)} testid={`plan-toggle-${p.slug}`}>{p.active ? <PowerOff className="w-3 h-3" /> : <Power className="w-3 h-3" />}</IconBtn>
                      <IconBtn title="Delete" danger onClick={() => del(p.id)} testid={`plan-delete-${p.slug}`}><Trash2 className="w-3 h-3" /></IconBtn>
                    </div>
                  </Td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="p-10 text-center text-zinc-500" data-testid="plans-empty">No plans configured.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {editing && (
        <PlanModal
          initial={editing}
          onClose={() => { setEditing(null); setShowNew(false); }}
          onSaved={() => { setEditing(null); setShowNew(false); load(); }}
          isNew={showNew}
        />
      )}
    </AdminLayout>
  );
}

function PlanModal({ initial, onClose, onSaved, isNew }) {
  const [f, setF] = useState({ ...EMPTY, ...initial, features: (initial.features || []).join("\n") });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...f,
        monthly_price: parseFloat(f.monthly_price) || 0,
        trial_days: parseInt(f.trial_days || 0),
        features: (f.features || "").split("\n").map((s) => s.trim()).filter(Boolean),
        stripe_price_id: f.stripe_price_id || null,
      };
      if (isNew) {
        await api.post("/admin/plans", payload);
        toast.success("Plan created");
      } else {
        await api.patch(`/admin/plans/${initial.id}`, payload);
        toast.success("Plan updated");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" onClick={onClose} data-testid="plan-modal">
      <div className="bg-ec-ink border border-white/10 rounded-sm max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h3 className="font-display text-xl font-bold text-white">{isNew ? "New plan" : "Edit plan"}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 grid sm:grid-cols-2 gap-4">
          <ModalField label="Slug *" disabled={!isNew}>
            <Input value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value.toLowerCase().replace(/\s+/g, "_") })} className="rounded-sm bg-white/5 border-white/10 text-white" disabled={!isNew} data-testid="plan-input-slug" />
          </ModalField>
          <ModalField label="Name *"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-sm bg-white/5 border-white/10 text-white" data-testid="plan-input-name" /></ModalField>
          <ModalField label="Target role">
            <select value={f.role_target} onChange={(e) => setF({ ...f, role_target: e.target.value })} className="rounded-sm h-10 px-3 bg-white/5 border border-white/10 text-white text-sm w-full" data-testid="plan-input-role">
              {["professional", "organization", "institution", "verifier", "api"].map((r) => <option key={r} value={r} className="bg-ec-ink">{r}</option>)}
            </select>
          </ModalField>
          <ModalField label="Monthly price (USD)"><Input type="number" step="0.01" value={f.monthly_price} onChange={(e) => setF({ ...f, monthly_price: e.target.value })} className="rounded-sm bg-white/5 border-white/10 text-white" data-testid="plan-input-price" /></ModalField>
          <ModalField label="Trial days"><Input type="number" value={f.trial_days} onChange={(e) => setF({ ...f, trial_days: e.target.value })} className="rounded-sm bg-white/5 border-white/10 text-white" data-testid="plan-input-trial" /></ModalField>
          <ModalField label="Stripe Price ID (optional)"><Input value={f.stripe_price_id || ""} onChange={(e) => setF({ ...f, stripe_price_id: e.target.value })} className="rounded-sm bg-white/5 border-white/10 text-white font-mono-plex" placeholder="price_xxx" data-testid="plan-input-stripe-id" /></ModalField>
          <div className="sm:col-span-2"><ModalField label="Description"><Input value={f.description || ""} onChange={(e) => setF({ ...f, description: e.target.value })} className="rounded-sm bg-white/5 border-white/10 text-white" data-testid="plan-input-description" /></ModalField></div>
          <div className="sm:col-span-2">
            <Label className="eyebrow text-zinc-500">Features (one per line)</Label>
            <textarea value={f.features} onChange={(e) => setF({ ...f, features: e.target.value })} rows={5} className="rounded-sm w-full mt-2 p-3 bg-white/5 border border-white/10 text-white text-sm font-mono-plex" placeholder="Unlimited certificates&#10;Priority support" data-testid="plan-input-features" />
          </div>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-white/10">
          <Button variant="outline" className="rounded-sm border-white/10 text-white hover:bg-white/10" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-sm bg-ec-emerald hover:bg-ec-emerald/90 text-ec-ink" data-testid="plan-save">{saving ? "Saving…" : "Save plan"}</Button>
        </div>
      </div>
    </div>
  );
}
function ModalField({ label, children, disabled }) {
  return (
    <div className={disabled ? "opacity-60" : ""}>
      <Label className="eyebrow text-zinc-500">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function Th({ children, className = "" }) { return <th className={`px-4 py-3 eyebrow text-zinc-500 ${className}`}>{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-4 py-3 align-middle ${className}`}>{children}</td>; }
function IconBtn({ children, onClick, title, danger, testid }) {
  return (
    <button type="button" title={title} onClick={onClick} className={`p-1.5 rounded-sm border transition-colors ${danger ? "border-ec-error/30 text-ec-error hover:bg-ec-error/10" : "border-white/10 text-zinc-300 hover:bg-white/10"}`} data-testid={testid}>
      {children}
    </button>
  );
}
